import React, {  useContext, useEffect,  useState, useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ModalDommus from "../../components/Modal";
import { WebSocketContext } from "../../contexts/WebSocketContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons";
import { Icon } from '@iconify/react';
import phoneOutgoing from '@iconify-icons/carbon/phone-outgoing';
import phoneBlock from '@iconify-icons/carbon/phone-block';
import "./style.scss";
import { VoipContext } from "../../contexts/VoipContext";
import { ocultaTelefone } from "../../components/InputTextMask";
import caAPI from "../../services/caAPI";
import NotificationDommus from "../../helpers/NotificationsHelper";
import { celularMask } from "../../components/InputTextMask";
import { infoToast } from "../../components/DommusToast";
import { AtendentesContext } from "../../contexts/AtendentesContext";
import { identificacaoTentativa, ehAtendenteOfertado, ofertaPerdidaParaOutroAtendente, ehAtendenteDesconsiderado, segundosAteDispensaOferta, temCicloDeTentativas } from "./tentativaVoip";

export default function RecebeLigacaoVoipModal() {
  const [modalAberto, setModalAberto] = useState(false);
  const [dadosChamada, setDadosChamada] = useState({});
  const [idTimeoutReenviarFila, setIdTimeoutReenviarFila] = useState(null);
  const [idsChamadasDesconsiderar, setIdsChamadasDesconsiderar] = useState([])
  const { channel } = useContext(WebSocketContext);
  const { validarUsuarioAtualAtendePorAtendimento } = useContext(AtendentesContext)
  const {
    inCallStatus, 
    connection, 
    triggerCloseModalIncoming, 
    receiveCall, 
    answerCall, 
    handleRejeitar, 
    hangUp, 
    adicionaChamadaPerdida,
    handleLigacaoPerdida
  } = useContext(VoipContext);

  /**
   * Comando único de redistribuição, usado tanto pela expiração quanto pela recusa. Quem não
   * é o atendente ofertado não pede nada: o backend responderia sem efeito, e insistir podia
   * tirar a chamada de quem realmente a recebeu.
   */
  const solicitarRedistribuicao = (data, motivo) => {
    const identificacao = identificacaoTentativa(data?.tentativa);
    const idUsuario = Number(caAPI.getLocalState('idUsuario'));
    if (!identificacao || !ehAtendenteOfertado(data.tentativa, idUsuario)) {
      return;
    }
    caAPI.atendimento.voip.redistribuir(Object.assign({ motivo }, identificacao))
      .catch(error => console.log(error));
  };

  /**
   * Gatilho redundante da expiração. O prazo chega pronto do backend, em segundos e já somado
   * à margem, então nada é recalculado aqui: o relógio do dispositivo pode estar deslocado e a
   * configuração bruta deixou de ser entregue justamente para não haver como recalcular.
   * O fechamento do modal não acontece aqui — quem fecha é o evento que o backend emitir.
   */
  const agendarGatilhoRedundante = (data, idUsuario) => {
    const tentativa = data.tentativa;
    if (!ehAtendenteOfertado(tentativa, idUsuario) || tentativa.segundosAteGatilhoRedundante == null) {
      return null;
    }
    return setTimeout(() => {
      solicitarRedistribuicao(data, 'expiracao');
    }, Number(tentativa.segundosAteGatilhoRedundante) * 1000);
  };

  /**
   * Dispensa local de uma oferta sem atendente ofertado — transbordo e oferta coletiva.
   *
   * Não emite comando algum ao backend: a redistribuição continua restrita ao atendente
   * ofertado. Aqui só se encerra a exibição local e registra-se a ligação perdida, porque quem
   * foi alcançado pelo transbordo e não atendeu perdeu a ligação de fato.
   *
   * `handleLigacaoPerdida()` fecha o modal pelo `triggerCloseModalIncoming`, então não é preciso
   * mexer em `modalAberto` aqui — mesma sequência da transferência não atendida.
   */
  const agendarDispensaOferta = (data) => {
    const segundos = segundosAteDispensaOferta(data.tentativa);
    if (segundos == null) {
      return null;
    }
    return setTimeout(() => {
      adicionaChamadaPerdida(data);
      handleLigacaoPerdida();
      setDadosChamada({});
    }, segundos * 1000);
  };

  /** Temporizador da transferência não atendida, preservado como está hoje. */
  const agendarTransferenciaPerdida = (data) => {
    return setTimeout(() => {
      registrarTransferenciaPerdida(data);
      handleLigacaoPerdida();
      setDadosChamada({});
    }, (Number(caAPI.getLocalState('tempoLimiteVoipSegundos')) || 30) * 1000);
  };

  const callbackVoip = useCallback(({ data }) => {
    let idUsuario = Number(caAPI.getLocalState('idUsuario'));
    // A mesma chamada foi ofertada a outro atendente. O backend não emite evento para quem
    // perdeu a oferta — a nova oferta é o único sinal que chega aqui, e sem tratá-la o modal
    // original permanece aberto durante toda a redistribuição.
    if (modalAberto && ofertaPerdidaParaOutroAtendente(dadosChamada.tentativa, data.tentativa, idUsuario)) {
      if (idTimeoutReenviarFila) {
        clearTimeout(idTimeoutReenviarFila);
        setIdTimeoutReenviarFila(null);
      }
      setModalAberto(false);
      setDadosChamada({});
      // Mesmo encerramento local do aceite recusado por tentativa concluída: o token do
      // agente anterior já foi substituído pela transferência na CCD.
      handleLigacaoPerdida();
      return;
    }
    if (
      (!inCallStatus) && 
      (!data.atendido || data.transferencia?.existe) &&
      (validarUsuarioAtualAtendePorAtendimento(data)) && 
      (
        (data.redistribuido === false && ((idUsuario === Number(data.atendente)) || (data.atendente === null))) || 
        (data.redistribuido && Number(data.redistribuido) !== idUsuario)
      ) && 
      (
        !data.idsUsuariosQualificados || 
        data.idsUsuariosQualificados.length === 0 || 
        (Array.isArray(data.idsUsuariosQualificados) && data.idsUsuariosQualificados.includes(idUsuario))
      ) &&
      // Transbordo: quem já recebeu esta chamada não volta a tocar. Somado ao filtro de
      // qualificados acima, nunca no lugar dele — qualificação é quem pode receber a oferta.
      (!ehAtendenteDesconsiderado(data.idsUsuariosDesconsiderar, idUsuario)) &&
      (data.transferencia?.existe || !idsChamadasDesconsiderar.includes(data.id_chamada))
    ) {
      // Transferência entre atendentes não tem ciclo de tentativas: o temporizador local
      // continua sendo o único relógio dela. Ligação recebida é o oposto — o relógio vive no
      // backend e o timer daqui é só redundância para o caso de a fila não consumir.
      // Nunca os dois: o gatilho redundante existe só para o atendente ofertado, e a dispensa
      // local só para a oferta que não tem nenhum. Sem a segunda, o transbordo ficaria sem
      // fallback justamente no caminho que substituiu a redistribuição individual.
      const idTimeout = data.transferencia?.existe
        ? agendarTransferenciaPerdida(data)
        : agendarGatilhoRedundante(data, idUsuario) || agendarDispensaOferta(data);
      // seta estado antes de receiveCall para garantir que dadosChamada está disponível
      // quando o evento incoming do Twilio disparar e setar connection
      setIdTimeoutReenviarFila(idTimeout);
      setModalAberto(true);
      setDadosChamada(data);
      receiveCall(data.connection_socket, data.id, identificacaoTentativa(data.tentativa), temCicloDeTentativas(data.tentativa))
      if(!connection && data.redistribuido) {
        caAPI.atendimento.voip.chamarAgent(data.id)
      }
    }
    
  }, [receiveCall, setModalAberto, setDadosChamada, inCallStatus, dadosChamada, idsChamadasDesconsiderar,
      modalAberto, idTimeoutReenviarFila, handleLigacaoPerdida]);

  const callbackVoipRedirecionar = useCallback(({data}) => {
    let idUsuario = Number(caAPI.getLocalState('idUsuario'));
    if ((Number(data.atendente) === idUsuario) || data.atendente == null) {
      // uma oferta devolvida conta como ligação perdida para este atendente; a única exceção
      // é redistribuição (data.redistribuido), que não é perda pessoal
      if(!data.redistribuido && (data.id !== dadosChamada.id)) {
        adicionaChamadaPerdida(data)
      }
      if(data.transferencia?.existe && (data.atendente != null)) {
        registrarTransferenciaPerdida(data)
      }else{
        // Atendente já em ligação devolvendo a oferta: é recusa, não ausência de resposta.
        solicitarRedistribuicao(data, 'recusa');
      }
      if(idTimeoutReenviarFila){
        clearTimeout(idTimeoutReenviarFila);
        setIdTimeoutReenviarFila(null);
      }
    }
  }, [idTimeoutReenviarFila, dadosChamada?.id]);

  useEffect(() => {
    // Unbind sempre direcionado ao próprio handler: 'voip.1' também é ouvido pelo dashboard, e o
    // unbind sem callback removeria todos os ouvintes do evento, não só os deste modal.
    const desligarHandlers = () => {
      if(channel){
        channel.unbind('voip.1', callbackVoip);
        channel.unbind('voip.1', callbackVoipRedirecionar);
      }
    };
    if(channel && callbackVoip && callbackVoipRedirecionar){
      desligarHandlers();
      if(!inCallStatus){
        channel.bind('voip.1', callbackVoip);
      }else{
        channel.bind('voip.1', callbackVoipRedirecionar);
      }
    }
    return desligarHandlers;
  }, [channel, inCallStatus, callbackVoip, callbackVoipRedirecionar, idTimeoutReenviarFila,modalAberto]);

  useEffect(() => {
    if(channel){
      channel.unbind('chamada-encerrada-para-modal');
      if(dadosChamada.id_chamada){
        channel.bind('chamada-encerrada-para-modal', (data) => {

          // só bloqueia o id_chamada quando a ligação foi atendida e depois encerrada;
          // encerramento sem atendimento significa redistribuição — o próximo voip.1 deve passar
          if(data.atendido) {
            setIdsChamadasDesconsiderar(ids => [...ids, data.idConteudoAtendimento])
          }
          if(Number(data.idConteudoAtendimento) === Number(dadosChamada.id_chamada)){
            // A chamada exibida encerrou: sem atendimento é perdida, atendida é só o fim da
            // ligação. `data.atendido` vem do backend; o antigo `origem.tempoTransferencia`
            // nunca existiu neste evento e só não estourava por causa do curto-circuito do ||
            if(!data.atendido) {
              adicionaChamadaPerdida(data)
              clearTimeout(idTimeoutReenviarFila);
              setIdTimeoutReenviarFila(null);
            } else {
              NotificationDommus.showNotification(`Ligação finalizada com ${celularMask(dadosChamada.contato)}`, 'Ligação Encerrada')
            }
              setModalAberto(false);
              setDadosChamada({});
              handleRejeitar()
            }
        });
      }
    }

    return () => {
      channel && channel.unbind('chamada-encerrada-para-modal');
    };
  }, [channel, dadosChamada.id_chamada, idTimeoutReenviarFila, idsChamadasDesconsiderar]);

  useEffect(() => {
    // Handler nomeado para o unbind ser direcionado: 'voip-assumido' também é ouvido pelo
    // dashboard, e o unbind sem callback removeria o ouvinte de lá junto com este.
    let aoAssumirVoip = null;
    if(channel){
      if(dadosChamada.id_chamada){
        aoAssumirVoip = (data) => {
            // só adiciona à lista de desconsiderar quando há atendente — redistribuição
            // (atendente null) não bloqueia o próximo voip.1 da mesma chamada
            if(data.atendente) {
              setIdsChamadasDesconsiderar(ids => [...ids, data.id_chamada])
            }
            if(Number(data.atendente) && ((Number(data.id_chamada) === Number(dadosChamada.id_chamada)) &&
                Number(data.atendente) !== Number(caAPI.getLocalState('idUsuario')))){
              if(dadosChamada.transferencia?.existe){
                infoToast.fire({text: "Transferência não atendida."})
              }else{
                infoToast.fire({text: "Ligação atendida por outro operador."})
              }
              clearTimeout(idTimeoutReenviarFila);
              setIdTimeoutReenviarFila(null);
              setModalAberto(false);
              setDadosChamada({});
              hangUp()
            }else if((Number(data.id_chamada) === Number(dadosChamada.id_chamada)) &&
            Number(data.atendente) == Number(caAPI.getLocalState('idUsuario'))){
                clearTimeout(idTimeoutReenviarFila);
                setIdTimeoutReenviarFila(null);
            }else if(Number(data.id_chamada) === Number(dadosChamada.id_chamada) && !data.atendente){
              // atendente null significa que a chamada foi devolvida à fila pelo backend —
              // fecha o modal localmente sem tostar (nova emissão voip.1 virá)
              clearTimeout(idTimeoutReenviarFila);
              setIdTimeoutReenviarFila(null);
              setModalAberto(false);
              setDadosChamada({});
              hangUp()
            }
        };
        channel.bind('voip-assumido', aoAssumirVoip);
      }
    }

    return () => {
      channel && aoAssumirVoip && channel.unbind('voip-assumido', aoAssumirVoip);
    };
  }, [channel, dadosChamada.id_chamada, connection, idTimeoutReenviarFila, hangUp]);

  useEffect(() => {
    if(triggerCloseModalIncoming){
      setModalAberto(false);
    }
  },[triggerCloseModalIncoming, setModalAberto]);

  useEffect(() => {
    if(((connection && connection?._status) === 'closed')){
      hangUp();
    }
  }, [connection, hangUp]);

  function handleAnswerCall() {
    if(idTimeoutReenviarFila){
      clearTimeout(idTimeoutReenviarFila);
      setIdTimeoutReenviarFila(null);
    }
    answerCall();
    setModalAberto(false);
  }

  function handleRejectCall() {
    let idUsuario = Number(caAPI.getLocalState('idUsuario'));
    if(dadosChamada){
      if(dadosChamada.transferencia?.existe){
        registrarTransferenciaPerdida(dadosChamada, 14);
      }else{
        // Recusa é antecipação da redistribuição pelo mesmo comando, só o motivo muda. Para
        // quem não é o atendente ofertado nada é pedido: encerra apenas a exibição local.
        solicitarRedistribuicao(dadosChamada, 'recusa');
      }
      if(idTimeoutReenviarFila){
        clearTimeout(idTimeoutReenviarFila);
        setIdTimeoutReenviarFila(null);
      }
      if(Number(dadosChamada.atendente) == idUsuario){
        handleRejeitar();
      }else{
        hangUp();
      }
      setDadosChamada({});
    }
    setModalAberto(false);
  }

  const registrarTransferenciaPerdida = (data, tipoHistorico = 13) => {
    if(data.transferencia?.existe && data.transferencia?.atendente_original){
      caAPI.atendimento.transferenciaNaoAtendida(data.id, data.transferencia.atendente_original, tipoHistorico)
        .then().catch(error => console.log(error));
    }
  };

  return (
    <div>
      <ModalDommus 
        size="sm" 
        backdrop="static" 
        zindex="1080" 
        open={!inCallStatus && connection && modalAberto} 
        titulo="Ligação Recebida"
        callbackAberturaModal={() => {
          if(dadosChamada?.contato) {
            NotificationDommus.showNotification(`${celularMask(dadosChamada.contato)} está ligando para você.`, 'Nova Ligação')
          }
        }}
      >
        <div className="corpoLigacaoRecebida">
          <div className="info">
            <div className="fotoEnvolvido">
              <FontAwesomeIcon icon={faUserCircle} color="#444444" />
            </div>
          </div>
          <br />
          <div className="text-center">

            {dadosChamada.nome ?? dadosChamada.nome}
            <br />
            <b>{ocultaTelefone(dadosChamada.contato)}</b>
          </div>
          <br />
          <div className="stage">
            <div className="dot-elastic" />
          </div>
          {(!inCallStatus) && connection &&
            <div className="botoesAcao">

              <OverlayTrigger key="buttonAtender" placement="top" overlay={<Tooltip id={`tooltip-atender`}>Atender</Tooltip>}>
                <button className="botaoAtender" onClick={handleAnswerCall}>
                  <Icon icon={phoneOutgoing} width="1em" />
                </button>
              </OverlayTrigger>

              <OverlayTrigger key="buttonRejeitar" placement="top" overlay={<Tooltip id={`tooltip-rejeitar`}>Rejeitar</Tooltip>}>
                <button className="botaoRejeitar" onClick={handleRejectCall}>
                  <Icon icon={phoneBlock} width="1em" />
                </button>
              </OverlayTrigger>

            </div>
          }
          <p className="text-center">Ligando para: {dadosChamada.label}</p>
        </div>
      </ModalDommus>
    </div >

  )
}
